import {
    AuctionStatus,
    GetFeaturedAuctions200ResponseAllOfDataInner,
} from '@viewbid/ts-angular-client';
import { AuctionBandState } from '@viewbid/ui';

export enum AuctionsStatus {
    upcoming = 'upcoming',
    closed = 'closed',
    open = 'open',
    autoHide = 'autoHideClosedLots',
}

// Map of auction categories to CSS classes
const categoryToClassMap: Map<string, string> = new Map<string, string>([
    ['clothing', 'bg-vb-category-clothing'],
    ['collectibles', 'bg-vb-category-collectibles'],
    ['fitness_equipment', 'bg-vb-category-fitness'],
    ['furniture_and_appliances', 'bg-vb-category-furniture'],
    ['hardware', 'bg-vb-category-hardware'],
    ['jewelry', 'bg-vb-category-jewelry'],
    ['kids_clothing', 'bg-vb-category-kids'],
    ['office_equipment', 'bg-vb-category-office'],
    ['online_retailer', 'bg-vb-category-online'],
    ['restaurant_equipment', 'bg-vb-category-restaurant'],
    ['third_party_seized_vehicle', 'bg-vb-category-third-party'],
    ['tools', 'bg-vb-category-tools'],
    ['vehicles_equip_ind_tools', 'bg-vb-category-vehicles'],
    ['default', 'bg-vb-category-default'],
]);

/**
 * Determines the status of an auction based on its start and end times.
 *
 * @param fromDatetime - The start date time.
 * @param toDatetime - The end date time.
 * @returns The status of the auction.
 */
export function getAuctionStatus(
    fromDatetime: string,
    toDatetime: string,
): string {
    if (!fromDatetime || !toDatetime) {
        return 'open';
    }

    const currentDate: Date = new Date();
    const auctionStartDate: Date = new Date(fromDatetime);
    const auctionEndDate: Date = new Date(toDatetime);

    if (currentDate < auctionStartDate) {
        return AuctionStatus.Public.toLowerCase();
    } else if (
        currentDate >= auctionStartDate &&
        currentDate < auctionEndDate
    ) {
        return AuctionStatus.Open.toLowerCase();
    } else {
        return AuctionStatus.Complete.toLowerCase();
    }
}

/**
 * Generates a descriptive text based on the auction status.
 *
 * @param fromDatetime - The start date time.
 * @param toDatetime - The end date time.
 * @returns A descriptive text of the auction status.
 */
export function getAuctionStatusText(
    fromDatetime: string,
    toDatetime: string,
): string {
    const auctionStatus: string = getAuctionStatus(fromDatetime, toDatetime);
    const auctionStatusTextMap: { [key: string]: string } = {
        open: 'starts closing in',
        [AuctionStatus.Public.toLowerCase()]: 'starts in',
        [AuctionStatus.Complete.toLowerCase()]: 'closed',
    };

    return auctionStatusTextMap[auctionStatus] || '';
}

/**
 * Converts milliseconds to a string representation of days and hours.
 *
 * @param milliseconds - The time in milliseconds to convert.
 * @returns A string representation of the time in days and hours.
 */
export function toDaysHours(milliseconds: number): string {
    const millisecondsInDay: number = 24 * 60 * 60 * 1000;
    const millisecondsInHour: number = 60 * 60 * 1000;
    const days: number = Math.floor(milliseconds / millisecondsInDay);
    const hours: number = Math.floor(
        (milliseconds % millisecondsInDay) / millisecondsInHour,
    );

    return `${days} d ${hours} h`;
}

/**
 * Updates auction status based on current time.
 *
 * @param auction - The auction to update status for
 * @returns The updated status text
 */
export function updateAuctionStatusText(
    fromDatetime: string,
    toDatetime: string,
): string {
    const currentTime: Date = new Date();
    const startTime: Date = new Date(fromDatetime);
    const endTime: Date = new Date(toDatetime);

    if (currentTime >= startTime && currentTime < endTime) {
        return 'first lot closes';
    } else if (currentTime >= endTime) {
        return 'closed on';
    } else {
        return 'starts in';
    }
}

/**
 * Updates the auction status based on current time.
 *
 * @param fromDatetime - The start date time
 * @param toDatetime - The end date time
 * @returns The updated auction status
 */
export function getUpdatedAuctionStatus(
    fromDatetime: string,
    toDatetime: string,
): AuctionStatus {
    const currentTime: Date = new Date();
    const startTime: Date = new Date(fromDatetime);
    const endTime: Date = new Date(toDatetime);

    if (currentTime >= startTime && currentTime < endTime) {
        return AuctionStatus.Open;
    } else if (currentTime >= endTime) {
        return AuctionStatus.Complete;
    } else {
        return AuctionStatus.Public;
    }
}

/**
 * Auction-level lifecycle for the lot list heading + info band. Driven by the
 * API status (per the info-band handoff: not the client clock alone); an
 * unknown status falls back to the clock-derived status.
 */
export function deriveAuctionBandState(auction: {
    status?: AuctionStatus;
    fromDatetime?: string;
    toDatetime?: string;
}): AuctionBandState {
    switch (auction.status) {
        case AuctionStatus.Open:
            return 'open';
        case AuctionStatus.Closed:
        case AuctionStatus.Complete:
            return 'closed';
        case AuctionStatus.Draft:
        case AuctionStatus.Review:
        case AuctionStatus.Public:
            return 'upcoming';
        default: {
            const derived: AuctionStatus = getUpdatedAuctionStatus(
                auction.fromDatetime ?? '',
                auction.toDatetime ?? '',
            );
            if (derived === AuctionStatus.Open) return 'open';
            if (derived === AuctionStatus.Complete) return 'closed';
            return 'upcoming';
        }
    }
}

export function firstLotSnipeWindowStart(auction: {
    firstLotCloseTime?: string | null;
    bidSnipeWindow?: number | null;
}): Date | null {
    if (!auction.firstLotCloseTime) return null;
    const closeTime: number = new Date(auction.firstLotCloseTime).getTime();
    return new Date(closeTime - (auction.bidSnipeWindow || 0) * 1000);
}

export function isAuctionClosing(
    auction: {
        fromDatetime?: string;
        toDatetime?: string;
        firstLotCloseTime?: string | null;
        bidSnipeWindow?: number | null;
    },
    now: Date = new Date(),
): boolean {
    if (!auction.fromDatetime || !auction.toDatetime) return false;
    const snipeWindowStart: Date | null = firstLotSnipeWindowStart(auction);
    if (!snipeWindowStart) return false;
    const start: Date = new Date(auction.fromDatetime);
    const end: Date = new Date(auction.toDatetime);
    if (now < start || now >= end) return false;
    return snipeWindowStart <= now;
}

export function getAuctionStatusBannerText(
    auction: GetFeaturedAuctions200ResponseAllOfDataInner,
): string {
    // Get current status based on time
    const currentStatus: string = getAuctionStatus(
        auction.fromDatetime,
        auction.toDatetime,
    );

    switch (currentStatus) {
        case AuctionStatus.Open.toLowerCase():
            return AuctionsStatus.open;
        case AuctionStatus.Public.toLowerCase():
            return AuctionsStatus.upcoming;
        case AuctionStatus.Complete.toLowerCase():
        case AuctionStatus.Closed.toLowerCase():
            return AuctionsStatus.closed;
        default:
            return AuctionsStatus.closed;
    }
}

/**
 * Checks the given category against the keys from categoryToClassMap.
 * Returns the corresponding CSS class if a match is found, otherwise returns the default class for 'restaurant'.
 *
 * @param category The category to check.
 * @returns The corresponding CSS class or the default class if no matching category is found.
 */
export function getCategoryClass(category: string): string {
    const defaultCategoryClass: string = 'bg-vb-category-default';
    if (!category)
        return categoryToClassMap.get('default') || defaultCategoryClass; // Default fallback

    const lowerCaseCategory: string = category.toLowerCase();

    return categoryToClassMap.get(lowerCaseCategory) || defaultCategoryClass; // Default fallback
}
